import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { AI_SATEI_URL, COMPANY, NAGOYA_16_WARDS } from '@/constants/company';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const regionalJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/regional#webpage`,
      url: `${siteUrl}/regional`,
      name: '名古屋16区に強い理由｜地域密着の不動産会社 クリアコーポレーション',
      description: '名古屋市16区（千種区・東区・北区・西区・中村区・中区・昭和区・瑞穂区・熱田区・中川区・港区・南区・守山区・緑区・名東区・天白区）全域に対応。地元ネットワーク・地域特性・スピード対応が強みの株式会社クリアコーポレーション。',
      inLanguage: 'ja',
    },
    {
      '@type': 'RealEstateAgent',
      '@id': `${siteUrl}/#organization`,
      name: '株式会社クリアコーポレーション',
      url: siteUrl,
      areaServed: [
        '千種区', '東区', '北区', '西区', '中村区', '中区',
        '昭和区', '瑞穂区', '熱田区', '中川区', '港区', '南区',
        '守山区', '緑区', '名東区', '天白区',
      ].map((ward) => ({
        '@type': 'AdministrativeArea',
        name: `名古屋市${ward}`,
      })),
    },
  ],
};

const reasons = [
  { num: '01', icon: 'ri-map-pin-2-line', title: '16区すべてに精通した地元ネットワーク', desc: '名古屋市内16区の相場・需要・地域特性を熟知しています。「この区はこの時期に動く」「この路線沿いは需要が高い」など、データだけでは見えない地元の実情をご提案に活かします。' },
  { num: '02', icon: 'ri-team-line', title: '地域の買主・投資家とのパイプ', desc: '長年の営業活動で築いた地元の買主・投資家ネットワークがあります。市場に出す前に買い手が見つかるケースも多く、スピーディーな売却が可能です。' },
  { num: '03', icon: 'ri-building-2-line', title: '地域特有の物件・事情への対応力', desc: '旗竿地・古家付き・共有持分・相続物件など、地域特有の難しい案件にも対応実績があります。他社で断られた案件もお気軽にご相談ください。' },
  { num: '04', icon: 'ri-shield-check-line', title: '宅建免許取得の地元業者として信頼', desc: '愛知県知事免許を取得した正規の宅建業者です。地元に根ざした事業者として、長期的な信頼関係を大切にしています。' },
];

export default function RegionalPage() {
  return (
    <div className="min-h-screen">
      <title>名古屋西区の不動産売却・買取｜16区対応 クリアコーポレーション</title>
      <meta name="description" content="名古屋西区の不動産売却・買取・査定は株式会社クリアコーポレーションへ。名古屋西区をはじめ16区（千種区・東区・北区・西区・中村区・中区・昭和区・瑞穂区・熱田区・中川区・港区・南区・守山区・緑区・名東区・天白区）全域に対応。地元ネットワーク・スピード対応が強み。" />
      <meta name="keywords" content="名古屋西区 不動産, 名古屋西区 不動産売却, 名古屋西区 不動産買取, 名古屋16区 不動産, 名古屋 地域密着 不動産" />
      <meta property="og:title" content="名古屋16区に強い理由｜地域密着の不動産会社 クリアコーポレーション" />
      <meta property="og:description" content="名古屋市16区全域に対応。地元ネットワーク・地域特性・スピード対応が強みの株式会社クリアコーポレーション。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/regional" />
      <script type="application/ld+json">{JSON.stringify(regionalJsonLd)}</script>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/aacbefec664568850695e2bec2b5d437.jpg" alt="名古屋16区" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A2B4A]/90 via-[#1A2B4A]/70 to-[#1A2B4A]/40" />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest mb-4 uppercase">Regional Strength</p>
            <h1 className="text-white text-3xl md:text-5xl font-bold mb-6 leading-tight">
              名古屋16区に<br />強い理由
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl leading-relaxed">
              地元密着だからこそ知っているエリア相場・需要・タイミング。<br />
              全国対応しながらも、名古屋の地元力が私たちの最大の強みです。
            </p>
          </div>
        </section>

        {/* Reasons */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#4A90E2] text-sm font-medium tracking-widest mb-3 uppercase">Why We're Strong</p>
              <h2 className="text-[#1A2B4A] text-2xl md:text-4xl font-bold">地域力の4つの理由</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reasons.map((r) => (
                <div key={r.num} className="border border-gray-100 rounded-2xl p-8 hover:border-[#4A90E2]/30 transition-colors duration-200">
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0">
                      <span className="text-[#4A90E2]/30 text-4xl font-bold leading-none">{r.num}</span>
                    </div>
                    <div>
                      <div className="w-10 h-10 flex items-center justify-center bg-[#F0F4FA] rounded-lg mb-4">
                        <i className={`${r.icon} text-[#1A2B4A] text-xl`} />
                      </div>
                      <h3 className="text-[#1A2B4A] font-bold text-lg mb-3">{r.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 16 Wards */}
        <section className="py-20 md:py-28 bg-[#F0F4FA]">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#4A90E2] text-sm font-medium tracking-widest mb-3 uppercase">Coverage</p>
              <h2 className="text-[#1A2B4A] text-2xl md:text-4xl font-bold mb-4">名古屋市16区 全域対応</h2>
              <p className="text-gray-500 text-base">下記エリアはすべて地元ネットワークを活かした対応が可能です。</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
              {NAGOYA_16_WARDS.map((ward) => (
                <div key={ward} className="bg-white rounded-xl py-4 px-3 text-center text-[#1A2B4A] font-medium text-sm border border-[#4A90E2]/20 hover:bg-[#1A2B4A] hover:text-white transition-colors duration-200 cursor-default">
                  {ward}
                </div>
              ))}
            </div>
            <div className="text-center">
              <div className="inline-flex items-center gap-3 bg-white rounded-full px-8 py-4 border border-[#4A90E2]/30">
                <i className="ri-global-line text-[#4A90E2] text-xl" />
                <span className="text-[#1A2B4A] font-semibold text-base">全国からのご相談もお受けします</span>
              </div>
            </div>
          </div>
        </section>

        {/* 西区特集セクション */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#4A90E2] text-sm font-medium tracking-widest mb-3 uppercase">Nishi Ward</p>
              <h2 className="text-[#1A2B4A] text-2xl md:text-4xl font-bold mb-4">名古屋西区の不動産売却・買取</h2>
              <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
                株式会社クリアコーポレーションは名古屋市西区に拠点を置く地元密着の不動産会社です。<br />
                名古屋西区の相場・需要・地域特性を熟知したスタッフが対応します。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                { icon: 'ri-map-pin-line', title: '名古屋西区に本社あり', desc: '愛知県名古屋市西区江向町に本社を構える地元業者。名古屋西区の不動産事情を誰よりも知っています。' },
                { icon: 'ri-home-smile-line', title: '西区の売却実績多数', desc: '名古屋西区内のマンション・戸建・土地・相続物件など、幅広い売却・買取実績があります。' },
                { icon: 'ri-time-line', title: '最短7日で現金化', desc: '名古屋西区の不動産AI査定は約60秒。査定後、最短7日での現金化も可能です。' },
              ].map((item) => (
                <div key={item.title} className="bg-[#F8F9FB] rounded-2xl p-7">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5">
                    <i className={`${item.icon} text-[#1A2B4A] text-2xl`} />
                  </div>
                  <h3 className="text-[#1A2B4A] font-bold text-base mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#F0F4FA] rounded-2xl p-8 md:p-10">
              <h3 className="text-[#1A2B4A] font-bold text-lg mb-4">名古屋西区の不動産売却・買取でよくあるご相談</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  '相続した名古屋西区の実家を売りたい',
                  '名古屋西区の空き家・古家を早く処分したい',
                  '名古屋西区のマンションの売却相場を知りたい',
                  '名古屋西区の土地をできるだけ高く売りたい',
                  '名古屋西区で住み替えを検討している',
                  '名古屋西区の不動産AI査定を試してみたい',
                ].map((text) => (
                  <li key={text} className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-[#4A90E2] mt-0.5 flex-shrink-0" />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
